import { DownloadOutlined, LoadingOutlined } from "@ant-design/icons";
import { Col, Row, Spin, Table, Input, Popover, Tag, Button, Tooltip } from "antd";
import { ColumnsType } from "antd/es/table";
import React, { useEffect, useState } from "react";
import BiometricStatsService from "../../Services/BiometricStats";
import dayjs, { Dayjs } from "dayjs";
import { IIdName } from "../../Services/CommonService";
import CustomRangePicker from "../../Common/Custom Components/Ant design/RangePicker";
import { SectionCard } from "../../Common/Layout";

const { Search } = Input;

interface ListDataType {
  key: string;
  companyName: string;
  allMembers: IIdName[];
}

interface ListData {
  items: ListDataType[];
  totalItems: number;
  totalCompletedCount: number;
}
const BiometricStats = ({ practiceId, practiceName }: { practiceId: number; practiceName: string }) => {

  const [loading, setLoading] = useState(false);
  const [listData, setListData] = useState<ListData>({
    items: [],
    totalItems: 0,
    totalCompletedCount: 0,
  });
  const [listParams, setListParams] = useState({
    search: "",
    currentPage: 1,
    pageSize: 15,
    sortDir: "ascend",
    sortCol: "Company.Name",
  });

  const [dateRange, setDateRange] = useState<[Dayjs | null, Dayjs | null]>([
    dayjs().startOf("month"),
    dayjs().endOf("day"),
  ]);

  const getCompanies = async () => {
    if (!practiceId) return;

    setLoading(true);
    try {
      const res = await BiometricStatsService.getBiometricStats(
        practiceId,
        (listParams.currentPage - 1) * listParams.pageSize,
        listParams.pageSize,
        listParams.search,
        dateRange[0]?.format("DD-MM-YYYY"),
        dateRange[1]?.format("DD-MM-YYYY"),
        listParams.sortCol,
        listParams.sortDir
      );

      const companies = res?.result?.items || [];

      setListData({
        items: companies.map((c: any) => ({
          key: c?.id,
          companyName: c?.companyName,
          allMembers: c?.allMembers || [],
        })),
        totalItems: res?.result?.totalRecords || 0,
        totalCompletedCount: res?.result?.totalCompletedCount || 0,
      });
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    getCompanies();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    practiceId,
    listParams.search,
    listParams.currentPage,
    listParams.pageSize,
    listParams.sortCol,
    listParams.sortDir,
    dateRange,
  ]);

  const handleDownload = async () => {
    if (!practiceId) return;

    try {
      const res = await BiometricStatsService.getBiometricStatsDownload(
        practiceId,
        practiceName,
        listParams.search,
        dateRange[0]?.format("DD-MM-YYYY"),
        dateRange[1]?.format("DD-MM-YYYY"),
        listParams.sortCol,
        listParams.sortDir
      );

      if (res.status === "done") {
        console.log("Download successful");
      } else {
        console.error("Download failed", res.error);
      }
    } catch (err) {
      console.error("Download failed", err);
    }
  };

  const columns: ColumnsType<ListDataType> = [
    {
      title: "S.No",
      width: "5%",
      render: (_, __, index) =>
        (listParams.currentPage - 1) * listParams.pageSize + index + 1,
    },
    {
      title: "Company Name",
      dataIndex: "companyName",
      key: "CompanyName",
      width: "25%",
      defaultSortOrder: "ascend",
      sorter: true,
    },
    {
      title: "Members",
      dataIndex: "allMembers",
      width: "25%",
      render: (_: any, record: ListDataType) => {
        const members = record?.allMembers || [];

        const name = members[0]?.name;
        const remainingCount = members?.length - 1;
        return (
          <>
            {members?.length > 0 && (
              <Row gutter={8}>
                <Col>
                  <span>{name}</span>
                </Col>
                <Col>
                  <Popover
                    placement="bottom"
                    //title={<>All Members List - <Tag color="blue">{members?.length}</Tag> </>}
                    content={
                      <Table
                        size="small"
                        pagination={false}
                        dataSource={members.map((x, i) => ({
                          key: i,
                          sno: i + 1,
                          name: x?.name,
                        }))}
                        columns={[
                          {
                            title: "S.No.",
                            dataIndex: "sno",
                            width: 60,
                          },
                          {
                            title: "Member name",
                            dataIndex: "name",
                          },

                        ]}
                      />
                    }
                  >
                    <div
                      style={{
                        maxWidth: 200,
                        whiteSpace: "nowrap",
                        overflow: "hidden",
                        textOverflow: "ellipsis",
                        cursor: "pointer",
                        display: "flex",
                        alignItems: "center",
                        gap: 6,
                      }}
                    >
                      {/* +N */}
                      {remainingCount > 0 && (
                        <Tag color="blue">+{remainingCount}</Tag>
                      )}
                    </div>
                  </Popover>
                </Col>
              </Row>
            )}
          </>
        );
      },
    },
    {
      title: "Total",
      dataIndex: "totalDone",
      width: "20%",
      render: (_: any, record: any) => (
        <div>
          {record.allMembers?.length}
        </div>
      ),
    }
  ];

  return (
    <SectionCard noPadding>
      <Row style={{ backgroundColor: "white" }}>
        <Col span={24}>
          <Row gutter={[8, 8]} justify="end" align="middle" className="py-2">
            <Col><Tag color="green">Total Completed  - {listData.totalCompletedCount}</Tag></Col>
            <Col>
              <Tooltip
                title="Download Biometric report"
                color="white"
                overlayInnerStyle={{ color: "rgba(0,0,0,0.85)" }}
              >
                <Button
                  type="default"
                  onClick={() => handleDownload()}
                  disabled={!listData.totalItems}
                  icon={<DownloadOutlined />}
                />
              </Tooltip>
            </Col>
            <Col xs={24} sm={12} md={5} lg={5} xl={4} xxl={3}>
              <Search
                placeholder="Search"
                onSearch={(val) =>
                  setListParams({
                    ...listParams,
                    search: val,
                    currentPage: 1,
                  })
                }
                onChange={(val) =>
                  val.target.value === ""
                    ? setListParams({
                      ...listParams,
                      search: "",
                      currentPage: 1,
                    })
                    : null
                }
              />
            </Col>

            <Col xs={24} sm={12} md={8} lg={6} xl={5} xxl={4}>
              <CustomRangePicker
                value={dateRange}
                allowClear={false}
                onChange={(dates) => {
                  setDateRange(dates ? (dates as [Dayjs, Dayjs]) : [null, null]);
                  setListParams((prev) => ({
                    ...prev,
                    currentPage: 1,
                  }));
                }}
              />
            </Col>
          </Row>

          <Table
            rowKey="key"
            dataSource={listData.items}
            columns={columns}
            size="small"
            scroll={
              (listData?.items?.length || 0) > 15
                ? { y: "calc(100vh - 340px)", x: "max-content" }
                : undefined
            }
            className="border-top-table"
            locale={{
              triggerDesc: "",
              triggerAsc: "",
              cancelSort: "",
            }}
            summary={() => {
              const totalCompletedCount = listData?.items?.reduce(
                (sum: number, item: any) => sum + (item?.allMembers?.length || 0),
                0
              );
              // align="right"
              return (
                <Table.Summary fixed>
                  <Table.Summary.Row
                    style={{
                      backgroundColor: "#f5f8ff",
                      fontWeight: "bold",
                      color: "#4d4d4d",
                    }}
                  >
                    <Table.Summary.Cell
                      index={1}
                      className="fw600"
                    ></Table.Summary.Cell>
                    <Table.Summary.Cell index={2} className="fw600">
                      Page Total
                    </Table.Summary.Cell>
                    <Table.Summary.Cell
                      index={3}
                      className="fw600"
                    ></Table.Summary.Cell>
                    <Table.Summary.Cell index={4}>
                      {totalCompletedCount}
                    </Table.Summary.Cell>
                  </Table.Summary.Row>
                </Table.Summary>
              );
            }}
            loading={{
              spinning: loading,
              indicator: <Spin indicator={<LoadingOutlined />} />,
            }}
            pagination={{
              showTotal: (total, range) =>
                `${range[0]}-${range[1]} of ${total} items`,
              current: listParams.currentPage,
              pageSize: listParams.pageSize,
              total: listData.totalItems,
              showSizeChanger: true,
              pageSizeOptions: ["15", "25", "50", "100"],
            }}
            onChange={(pagination, _filters, sorter: any) => {
              setListParams((prev) => ({
                ...prev,
                currentPage: pagination.current || 1,
                pageSize: pagination.pageSize || 15,
                sortCol: sorter?.order ? sorter.columnKey : undefined,
                sortDir: sorter?.order || undefined,
              }));
            }}
          />
        </Col>
      </Row>
    </SectionCard>

  );
};

export default BiometricStats;
